import React, { useState } from 'react';
import { Calculator, Copy, Trash2, PiggyBank } from 'lucide-react';

const SavingsCalculatorTool = () => {
  const [monthlySavings, setMonthlySavings] = useState('');
  const [interestRate, setInterestRate] = useState('');
  const [time, setTime] = useState('');
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const calculate = () => {
    const monthly = parseFloat(monthlySavings);
    const rate = parseFloat(interestRate);
    const years = parseFloat(time);

    if (isNaN(monthly) || isNaN(rate) || isNaN(years) || monthly <= 0 || years <= 0) {
      setResult(null);
      return;
    }

    const monthlyRate = rate / 100 / 12;
    const totalMonths = years * 12;

    // Future Value of monthly savings (ordinary annuity)
    // FV = PMT * [((1 + r)^n - 1) / r]
    let totalSavings;
    if (monthlyRate === 0) {
      totalSavings = monthly * totalMonths;
    } else {
      totalSavings = monthly * ((Math.pow(1 + monthlyRate, totalMonths) - 1) / monthlyRate);
    }

    const totalDeposits = monthly * totalMonths;
    const interestEarned = totalSavings - totalDeposits;
    const interestPercent = (interestEarned / totalDeposits) * 100;

    setResult({
      monthly,
      rate,
      years,
      totalMonths,
      totalSavings,
      totalDeposits,
      interestEarned,
      interestPercent
    });
  };

  const copyResult = (value) => {
    navigator.clipboard.writeText(value.toFixed(2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const clearAll = () => {
    setMonthlySavings('');
    setInterestRate('');
    setTime('');
    setResult(null);
  };

  const formatCurrency = (value) => {
    return '$' + value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  return (
    <div className="space-y-6">
      {/* Input Section */}
      <div className="p-4 bg-gray-800 rounded-lg space-y-4">
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Monthly Savings ($)
            </label>
            <input
              type="number"
              value={monthlySavings}
              onChange={(e) => setMonthlySavings(e.target.value)}
              placeholder="e.g., 500"
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
              data-testid="monthly-savings-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Interest Rate (% per year)
            </label>
            <input
              type="number"
              step="0.01"
              value={interestRate}
              onChange={(e) => setInterestRate(e.target.value)}
              placeholder="e.g., 5"
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
              data-testid="interest-rate-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Time (years)
            </label>
            <input
              type="number"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              placeholder="e.g., 10"
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
              data-testid="time-input"
            />
          </div>
        </div>
      </div>

      {/* Calculate Button */}
      <button
        onClick={calculate}
        className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium"
        data-testid="calculate-savings-btn"
      >
        <Calculator size={18} />
        Calculate Savings
      </button>

      {/* Results Section */}
      {result && (
        <div className="p-4 bg-gray-800 rounded-lg space-y-4" data-testid="savings-results">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="p-4 bg-gray-900 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-400">Total Savings</span>
                <button
                  onClick={() => copyResult(result.totalSavings)}
                  className={`p-1.5 rounded transition-colors ${
                    copied ? 'bg-green-600' : 'bg-gray-700 hover:bg-gray-600'
                  }`}
                  data-testid="copy-total-savings"
                >
                  <Copy size={14} />
                </button>
              </div>
              <div className="text-2xl font-bold text-green-400" data-testid="total-savings-result">
                {formatCurrency(result.totalSavings)}
              </div>
            </div>

            <div className="p-4 bg-gray-900 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-400">Interest Earned</span>
                <button
                  onClick={() => copyResult(result.interestEarned)}
                  className={`p-1.5 rounded transition-colors ${
                    copied ? 'bg-green-600' : 'bg-gray-700 hover:bg-gray-600'
                  }`}
                  data-testid="copy-interest-earned"
                >
                  <Copy size={14} />
                </button>
              </div>
              <div className="text-2xl font-bold text-yellow-400" data-testid="interest-earned-result">
                {formatCurrency(result.interestEarned)}
              </div>
              <div className="text-sm text-gray-500 mt-1">
                +{result.interestPercent.toFixed(1)}% bonus
              </div>
            </div>
          </div>

          {/* Visual Breakdown */}
          <div className="p-4 bg-gray-900 rounded-lg">
            <div className="flex items-center gap-2 mb-3">
              <PiggyBank size={16} className="text-green-400" />
              <span className="text-sm font-medium text-gray-300">Savings Breakdown</span>
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <div className="w-24 text-xs text-gray-400">Deposits</div>
                <div className="flex-1 h-6 bg-gray-700 rounded overflow-hidden">
                  <div 
                    className="h-full bg-blue-500 rounded"
                    style={{ width: `${(result.totalDeposits / result.totalSavings) * 100}%` }}
                  />
                </div>
                <div className="w-20 text-xs text-right text-gray-400">
                  {formatCurrency(result.totalDeposits)}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-24 text-xs text-gray-400">Interest</div>
                <div className="flex-1 h-6 bg-gray-700 rounded overflow-hidden">
                  <div 
                    className="h-full bg-yellow-500 rounded"
                    style={{ width: `${(result.interestEarned / result.totalSavings) * 100}%` }}
                  />
                </div>
                <div className="w-20 text-xs text-right text-gray-400">
                  {formatCurrency(result.interestEarned)}
                </div>
              </div>
            </div>
          </div>

          {/* Summary */}
          <div className="border-t border-gray-700 pt-4">
            <h4 className="text-sm font-medium text-gray-300 mb-3">Savings Summary</h4>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Monthly Savings</span>
                <span className="text-white font-medium">{formatCurrency(result.monthly)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Interest Rate</span>
                <span className="text-white font-medium">{result.rate}% per year</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Time Period</span>
                <span className="text-white font-medium">{result.years} years ({result.totalMonths} months)</span>
              </div>
              <div className="flex justify-between text-sm border-t border-gray-700 pt-2 mt-2">
                <span className="text-gray-400">Total Deposited</span>
                <span className="text-blue-400 font-medium">{formatCurrency(result.totalDeposits)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Interest Earned</span>
                <span className="text-yellow-400 font-medium">{formatCurrency(result.interestEarned)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Total Savings</span>
                <span className="text-green-400 font-medium">{formatCurrency(result.totalSavings)}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Info Section */}
      <div className="p-4 bg-gray-800/50 rounded-lg">
        <h3 className="text-sm font-medium text-gray-300 mb-3">Current Savings Rates</h3>
        <div className="grid md:grid-cols-3 gap-4 text-sm text-gray-400">
          <div>
            <div className="text-gray-500 mb-1">High-Yield Savings</div>
            <div>4% - 5% APY</div>
          </div>
          <div>
            <div className="text-gray-500 mb-1">Money Market</div>
            <div>3% - 4.5% APY</div>
          </div>
          <div>
            <div className="text-gray-500 mb-1">Traditional Savings</div>
            <div>0.01% - 0.5% APY</div>
          </div>
        </div>
      </div>

      <div className="p-4 bg-gray-800/50 rounded-lg">
        <h3 className="text-sm font-medium text-gray-300 mb-2">Savings Tips</h3>
        <div className="text-sm text-gray-400 space-y-1">
          <div>• Automate your savings with direct deposit</div>
          <div>• Choose high-yield savings accounts for better returns</div>
          <div>• Aim to save at least 20% of your income</div>
        </div>
      </div>

      {/* Clear Button */}
      <button
        onClick={clearAll}
        className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
        data-testid="clear-savings-btn"
      >
        <Trash2 size={16} />
        Clear All
      </button>
    </div>
  );
};

export default SavingsCalculatorTool;
